// client/src/features/quotations/useCatalogueSubmissionMarkers.ts
//
// THE CATALOGUE-ADDITION REVIEW QUEUE'S MARK ON A QUOTATION LINE.
//
// A line priced from a typed catalogue addition (`ItemLookupPanel`'s "+ Add
// this item to Master Catalogue") carries no field of its own saying so —
// `catalogueSubmission.service.getSubmissionsForQuotation`'s own header
// explains why nothing about a submission is denormalized onto the line:
// a submission can be decided long after the quotation that raised it was
// last saved, and the line has nowhere honest to keep a copy in sync from.
// So the mark is read fresh, ONCE per quotation load, and held here as a map
// by `lineIndex` rather than fetched per line.
//
// The owner's decisions (verbatim, do not re-litigate):
//   - held     -> "manual price · joins the catalogue when this quotation is sent"
//                 (2026-09-28: released at sent/finalized, see the server's
//                 `releaseHeldSubmissionsForQuotation`)
//   - pending  -> "manual price · waiting for catalogue review"
//   - rejected -> "manual price · catalogue addition refused — "<reason>""
//                 (the line KEEPS its price; the quote may already have gone
//                 to the vessel, and removing a number they have seen is
//                 worse than annotating it)
//   - approved -> no marker at all — it is an ordinary catalogue-backed line now.

import { useCallback, useEffect, useState } from 'react';
import { useLatestRequest } from '../../hooks/useLatestRequest';
import {
  getSubmissionsForQuotation,
  type QuotationCatalogueSubmissionMarker,
} from '../../lib/catalogueSubmissionsApi';

/** The line marker text for one submission, or null when none is owed. */
export function catalogueSubmissionLineMarker(
  submission: QuotationCatalogueSubmissionMarker | null | undefined
): string | null {
  if (!submission) return null;
  if (submission.status === 'held') return 'manual price · joins the catalogue when this quotation is sent';
  if (submission.status === 'pending') return 'manual price · waiting for catalogue review';
  if (submission.status === 'rejected') {
    return `manual price · catalogue addition refused — "${submission.decisionReason || ''}"`;
  }
  return null;
}

// `status` is read only to re-fetch when it changes: sent/finalized releases
// every held submission (straight to approved for an admin, to pending
// otherwise), and the map read before that would keep the held marker on
// screen until a reload (QA 2026-09-29, QUO-00193).
export function useCatalogueSubmissionMarkers(
  quotationId: string | null | undefined,
  status?: string | null
) {
  const [byLineIndex, setByLineIndex] = useState<Map<number, QuotationCatalogueSubmissionMarker>>(
    () => new Map()
  );

  // Only the newest request may write: an older, slower response must not
  // overwrite a newer one (a status change fires a second read while the first
  // is still in flight).
  const begin = useLatestRequest();

  const refresh = useCallback(async () => {
    const isCurrent = begin();
    if (!quotationId) {
      setByLineIndex(new Map());
      return;
    }
    const res = await getSubmissionsForQuotation(quotationId);
    if (!isCurrent()) return;
    const next = new Map<number, QuotationCatalogueSubmissionMarker>();
    for (const row of res.data || []) {
      if (row.lineIndex == null) continue;
      next.set(row.lineIndex, row);
    }
    setByLineIndex(next);
  }, [quotationId, begin]);

  useEffect(() => {
    let cancelled = false;
    refresh().catch((err) => {
      // A marker is an aid, not the money: the line prices and saves fine
      // without it, exactly as `useSupplierOptionCounts` treats its own
      // best-effort badge. Logged, not raised onto the page as an error
      // nobody here can act on.
      if (!cancelled) console.error('Failed to load catalogue submission markers:', err);
    });
    return () => {
      cancelled = true;
      begin(); // unmounted or re-keyed: nothing in flight may write
    };
    // `status` is not read inside `refresh`: it is here to re-run the read when
    // the quotation is sent or finalized.
  }, [refresh, status, begin]);

  const markerFor = useCallback(
    (lineIndex: number): string | null => catalogueSubmissionLineMarker(byLineIndex.get(lineIndex)),
    [byLineIndex]
  );

  return { markerFor, refresh };
}

export default useCatalogueSubmissionMarkers;
